import { Directive, ElementRef, OnInit } from '@angular/core';
import DOMPurify from 'dompurify';

@Directive({
    selector: '[plainPaste]',
    standalone: false
})
export class PlainPasteDirective implements OnInit {
  constructor(private el: ElementRef) {}

  ngOnInit() {
    // Attach listener with capture enabled
    this.el.nativeElement.addEventListener('paste', this.onPaste.bind(this), true);
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    let htmlData = event.clipboardData?.getData('text/html');
    let output = '';

    if (htmlData) {
      // Create a temporary container to process the pasted HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlData;

      // Convert inline <span> elements with bold styles to <strong> tags.
      const spanElements = tempDiv.querySelectorAll('span');
      spanElements.forEach(span => {
        const fontWeight = span.style.fontWeight;
        if (fontWeight === 'bold' || (parseInt(fontWeight) >= 700)) {
          const strong = document.createElement('strong');
          strong.innerHTML = span.innerHTML;
          span.parentNode?.replaceChild(strong, span);
        }
      });

      // Process <p> elements:
      // Replace each <p> with its innerHTML wrapped in a <span> and add <br> if needed.
      const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
      paragraphs.forEach((p, index) => {
        const span = document.createElement('span');
        span.innerHTML = p.innerHTML;
        if (index < paragraphs.length - 1) {
          span.innerHTML += '<br>';
        }
        p.parentNode?.replaceChild(span, p);
      });

      const processedHtml = tempDiv.innerHTML;

      // Use DOMPurify to allow only <strong> and <br> tags.
      output = DOMPurify.sanitize(processedHtml, { ALLOWED_TAGS: ['strong', 'br'], ALLOWED_ATTR: [] });
    } else {
      // Fallback: if no HTML is provided, use plain text.
      output = event.clipboardData?.getData('text/plain') || '';
    }

    // Remove any unwanted leading/trailing whitespace.
    output = output.replace(/^\s+|\s+$/g, '');

    // Convert any <br> separators into distinct paragraphs.
    output = this.convertBrToParagraphs(output);

    // Insert the transformed output.
    document.execCommand('insertHTML', false, output);
  }

  convertBrToParagraphs(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;

    // If the container doesn't already contain <p> elements,
    // check for <br> tags and split accordingly.
    if (container.querySelector('p') === null && html.indexOf('<br') !== -1) {
      // Split on <br> tags.
      const parts = html.split(/<br\s*\/?>/i);
      let newHtml = '';
      parts.forEach(part => {
        const trimmed = part.trim();
        if (trimmed) {
          newHtml += `<p>${trimmed}</p>`;
        }
      });
      return newHtml;
    } else {
      // Otherwise, if there is already a <p> wrapper,
      // process each <p> individually.
      const pElements = container.querySelectorAll('p');
      if (pElements.length > 0) {
        let newHtml = '';
        pElements.forEach(p => {
          // If a <p> contains <br>, split its innerHTML.
          if (p.innerHTML.indexOf('<br') !== -1) {
            const parts = p.innerHTML.split(/<br\s*\/?>/i);
            parts.forEach(part => {
              const trimmed = part.trim();
              if (trimmed) {
                newHtml += `<p>${trimmed}</p>`;
              }
            });
          } else {
            newHtml += `<p>${p.innerHTML.trim()}</p>`;
          }
        });
        return newHtml;
      }
      return html;
    }
  }
}
